<template>
  <div class="bulk-order-gift-cards-edit">
    <div class="container-header">
      <PageHeadlineForBulkOrder
        class="gift-card-header"
        title="ギフトカード作成"
        :show-stepper="false"
        :button-type="ButtonType.LINK"
        :button-label="giftCardForm.title"
        :button-icon-path="EditOutlineImage"
        button-icon-alt="パターン名称の編集"
        @clicked="onClickHeaderPatternEditButton"
      />
      <FlexibleModal
        v-if="showablePatternEditModal"
        title-text="パターン名称を変更"
        primary-button-text="完了"
        cancel-button-text="キャンセル"
        :primary-button-disabled="!isDraftPatternTitleInputted"
        @clicked-modal-cover="closeHeaderPatternEditModal"
        @clicked-primary="updatePatternTitle"
        @clicked-cancel="closeHeaderPatternEditModal"
      >
        <div class="gift-card-modal">
          <div class="gift-card-modal__caption">
            <p>
              まとめてオーダーでは、<br />
              複数のギフトカードが作成できます。
            </p>
            <p>
              パターンの名称を変更しておくと、<br />
              納品時にカードの種類が分かりやすくなります。
            </p>
          </div>
          <img
            src="../../../../../../images/bulk_personal_orders/pattern_edit_description.png"
            class="gift-card-modal__image"
          />
          <div class="gift-card-modal__label">
            <span>パターン名</span>
          </div>
          <input
            v-model.trim="draftTitle"
            type="text"
            :class="{
              'input-form--inputted': isDraftPatternTitleInputted,
              'input-form--inputting': isDraftPatternTitleFocused
            }"
            class="input-form"
            placeholder="例）職場用/家族用/スタバ500円 等"
            maxlength="8"
            @focus="onDraftPatternTitleFocus"
            @blur="onDraftPatternTitleBlur"
          />
          <p class="gift-card-modal__notice my-16">
            8文字まで設定可能です<br />
            ※お相手には表示されません
          </p>
        </div>
      </FlexibleModal>
    </div>
    <Form v-slot="{ errorBag, meta }" as="div">
      <div class="container">
        <div class="gift-card">
          <div v-if="errorMessages.length >= 1" class="gift-card__alert-box">
            <p
              v-for="(errorMessage, index) in errorMessages"
              :key="index"
              class="gift-card__alert-box--message"
            >
              {{ errorMessage }}
            </p>
          </div>
          <div class="gift-card__required_caution">
            必須
          </div>
          <div class="gift-card__section">
            <PageHeadlineForSection title="1. カードデザインを選ぶ" required />
            <div class="gift-card__section--content">
              <p class="gift-card__notice mb-16">
                gifteeのカードデザインの中からご指定いただくか、お客様にご用意いただいた画像を設定するかお選びください
              </p>
              <div class="radio_btn check-list-radio-btn gift-card__radio">
                <input
                  id="card_design"
                  v-model="cardDesignType"
                  type="radio"
                  :value="cardDesignTypeConstants.NORMAL"
                />
                <label for="card_design" class="radio-label">
                  カードデザインの中から選ぶ
                </label>
              </div>
              <div v-show="isCardDesign" class="my-16">
                <div
                  v-if="hasCardDesign"
                  class="gift-card__card-design-content"
                >
                  <ImgLazy
                    :image-url="giftCardForm.cardDesignImageUrl"
                    alt-text="カードデザイン"
                    aspect-ratio="1"
                    class="gift-card__card-design-image"
                  />
                  <div class="gift-card__card-design-change-link">
                    <label class="text-link" @click="onClickChangeCardDesign">
                      <span class="gift-card__sync-outline">
                        変更する
                      </span>
                    </label>
                  </div>
                </div>
                <ButtonSelectArea
                  v-else
                  label="カードデザイン一覧から選択する"
                  @click-button="onClickCardDesignButton"
                />
              </div>
              <div
                class="radio_btn check-list-radio-btn gift-card__radio"
                :class="{ 'mt-16': isPersonalCardDesign }"
              >
                <input
                  id="personal_card_design"
                  v-model="cardDesignType"
                  type="radio"
                  :value="cardDesignTypeConstants.PERSONAL"
                />
                <label for="personal_card_design" class="radio-label">
                  オリジナルの画像を使う
                </label>
              </div>
              <div v-show="isPersonalCardDesign" class="mt-16">
                <p class="gift-card__caution">
                  ご利用希望の画像のアップロードをお願いいたします
                </p>
                <p class="gift-card__notice my-16">
                  以下の条件を満たす画像のみご利用いただけます<br />
                  ・縦横比：1対1（正方形）<br />
                  ・容量：4mb以下<br />
                  ・形式：JPG/PNG/GIF のいずれか<br />
                  ※あまり小さな画像ですとギフトカード上に表示された際に粗くなってしまう可能性がございますので、「700*700ピクセル」以上のサイズを推奨しております。
                </p>
                <div class="gift-card__note">
                  <p>【オリジナル画像をご利用いただく際の注意事項】</p>
                  <p>
                    <strong>
                      著作権・肖像権・商標権等の侵害に当たらず、当社または第三者に対し誹謗中傷、差別、脅迫、いやがらせ、その他経済的・精神的な損害や不利益を与えないもののみを使用可能
                    </strong>
                    とします。
                  </p>
                  <p>
                    ギフトの納品後でも、外部から指摘を受けるなどして侵害に当たると判断した場合には、利用者の許諾なく画像削除または差し替えの対応をさせていただきます。
                  </p>
                  <br />
                  <p>（具体的なNG例）</p>
                  <p>
                    ・著作権で保護されているキャラクターのデザインを使用すること
                  </p>
                  <p>
                    ・既存の作品を利用して、二次的に創作した作品を使用すること
                  </p>
                  <p>
                    ・第三者が撮影した写真等を、本人の許諾なく使用すること
                  </p>
                  <p>
                    ・人物が特定できる写真の場合、相手の許可なく撮影した写真を使用すること
                  </p>
                  <p>
                    ・本サービスでの利用を許諾していない人物の写真を使用すること
                  </p>
                  <p>
                    ご提出いただく前に、画像が上記項目に該当しないことをご確認ください。
                  </p>
                </div>
                <p class="gift-card__notice my-16">
                  上記注意事項の内容に同意のうえ、画像のご指定をお願いしております
                </p>
                <label for="agreement_check" class="gift-card__agreement-check">
                  <div
                    :class="{
                      checkbox: !giftCardForm.isAgreementForPersonalCardDesign,
                      'checkbox--checked':
                        giftCardForm.isAgreementForPersonalCardDesign
                    }"
                  >
                    <input
                      id="agreement_check"
                      :value="giftCardForm.isAgreementForPersonalCardDesign"
                      type="checkbox"
                      @change="onChangeIsAgreementForPersonalCardDesign"
                    />
                    同意する
                  </div>
                </label>
                <div v-show="giftCardForm.isAgreementForPersonalCardDesign">
                  <div
                    v-show="hasPersonalCardDesignError"
                    class="gift-card__card-design-error-message"
                  >
                    <p
                      v-for="(fileErrorMessage, index) in fileErrorMessages"
                      :key="index"
                    >
                      {{ fileErrorMessage }}
                    </p>
                  </div>
                  <div v-show="hasUploadedPersonalCardDesignImage">
                    <div class="gift-card__personal-card-design-content">
                      <img
                        :src="giftCardForm.personalCardDesignDataUri"
                        alt="カードデザイン"
                        class="gift-card__personal-card-design-image"
                      />
                      <div class="gift-card__card-design-change-link">
                        <label
                          for="personal_card_design_upload"
                          class="text-link"
                        >
                          <span class="gift-card__sync-outline">
                            変更する
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <FileUploadArea
                    v-show="!hasUploadedPersonalCardDesignImage"
                    id="personal_card_design_upload"
                    label="画像アップロード"
                    :has-error="hasPersonalCardDesignError"
                    @change-file="onChangePersonalCardDesignFile"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="gift-card__section">
            <PageHeadlineForSection title="2. メッセージを入力する" />
            <div class="gift-card__section--content">
              <p class="gift-card__notice mb-16">
                メッセージは300文字以内でご入力ください
              </p>
              <InputFormTextArea
                :value="giftCardForm.message"
                name="message"
                :placeholder="
                  `例：\n今月もおつかれさまでした！\nささやかですが、カフェに行った時にでも使ってください。`
                "
                max-length="300"
                rules="max:300"
                :input-expand="true"
                :show-error-messages="false"
                @input="onChangeMessage"
              />
              <div v-show="errorBag['message']" class="gift-card__form--errors">
                <ErrorMessages :errors="errorBag['message']" as="p" />
              </div>
            </div>
          </div>
          <div class="gift-card__section">
            <PageHeadlineForSection
              title="3. あなたのお名前を記入する"
              required
            />
            <div class="gift-card__section--content">
              <div class="gift-card__notice mb-16">
                <p>今回のギフトの贈り主として表示されます</p>
                <p>20文字以内でご入力ください</p>
              </div>
              <InputFormText
                :value="giftCardForm.senderName"
                name="sender_name"
                placeholder="あなたのお名前（20文字以内）"
                max-length="20"
                rules="required|max:20"
                :show-error-messages="false"
                @input="onChangeSenderName"
              />
              <div
                v-show="errorBag['sender_name']"
                class="gift-card__form--errors"
              >
                <ErrorMessages :errors="errorBag['sender_name']" as="p" />
              </div>
            </div>
          </div>
          <div class="gift-card__section">
            <PageHeadlineForSection title="4. ギフトを選ぶ" required />
            <div class="gift-card__section--content">
              <p class="gift-card__notice mb-16">
                最大10枚まで一度に購入することができます
              </p>
              <ButtonSelectArea
                v-if="giftCardForm.itemSkuContentAttributes.length === 0"
                label="ギフト一覧から選択する"
                @click-button="onClickAddGiftButton"
              />
              <div v-else>
                <div>
                  <!-- eslint-disable prettier/prettier v-forの部分でvue/html-indentとの競合で警告が解決できなくなるのでdisableする -->
                  <EditableTicket
                    v-for="(itemSkuContentAttribute,
                            index) in giftCardForm.itemSkuContentAttributes"
                    :id="'select-item__box--' + index"
                    :key="index"
                    class="select-item__box"
                    :image-url="itemSkuContentAttribute.imageUrl"
                    :item-id="itemSkuContentAttribute.itemId"
                    :item-name="itemSkuContentAttribute.itemName"
                    :item-sku-id="itemSkuContentAttribute.itemSkuId"
                    :item-sku-name="itemSkuContentAttribute.hasMultipleItemSkus ? itemSkuContentAttribute.itemSkuName : ''"
                    :brand-name="itemSkuContentAttribute.brandName"
                    :expired-date="itemSkuContentAttribute.expiredDate"
                    :price="itemSkuContentAttribute.price"
                    :quantity="itemSkuContentAttribute.quantity"
                    :is-ticket-disabled="false"
                    :is-including-tax="itemSkuContentAttribute.isIncludingTax"
                    :is-plus-btn-active="itemSkuContentAttribute.isPlusBtnActive"
                    :is-minus-btn-active="itemSkuContentAttribute.isMinusBtnActive"
                    @click-count-up="onClickCountUp"
                    @click-count-down="onClickCountDown"
                    @click-delete-button="onClickDeleteItemSkuButton"
                  />
                </div>
                <TextModal
                  v-show="isDeleteItemSkuModalActive"
                  title-text="本当に削除しますか？"
                  caption-text="ギフト情報が削除されます。"
                  primary-button-text="削除する"
                  cancel-button-text="キャンセル"
                  @clicked-modal-cover="closeDeleteItemSkuModal"
                  @clicked-primary="onClickedPrimaryDeleteItemSkuModal(index)"
                  @clicked-cancel="closeDeleteItemSkuModal"
                />
                <TextLinkSelectArea
                  class="gift-card__select-items"
                  label="ギフト一覧から選択する"
                  @click-button="onClickAddGiftButton"
                />
              </div>
            </div>
          </div>
          <div class="gift-card__section">
            <PageHeadlineForSection
              title="5. このギフトカードの発行数"
              required
            />
            <div class="gift-card__section--content">
              <div class="gift-card__notice mb-16">
                <p>ギフトカードの発行数は1〜10000の値で入力してください</p>
              </div>
              <div class="gift-card__form">
                <InputFormNumber
                  :value="giftCardForm.publishAmount"
                  name="publish_amount"
                  placeholder="入力してください"
                  rules="required|between:1,10000"
                  :show-error-messages="false"
                  @input="onChangePublishAmount"
                />
              </div>
              <div
                v-show="errorBag['publish_amount']"
                class="gift-card__form--errors"
              >
                <ErrorMessages
                  :errors="errorBag['publish_amount']"
                  as="p"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="gift-card-fixed-bottom">
        <div class="gift-card-fixed-bottom__content">
          <ButtonPrimary
            text="プレビューに進む"
            :disabled="!meta.valid || !validForm(giftCardSessionToken) || isSubmitting || null"
            @click-primary-button="onClickPreviewButton"
          />
        </div>
      </div>
    </Form>
    <div class="container mt-16">
      <div class="gift-card-price">
        <div class="gift-card-price__total">
          <div class="gift-card-price__total--label">
            <span class="total-price__label">{{ giftCardForm.title }} 合計金額</span>
            <span v-show="isCurrentGiftCardPatternPriceIncludingTax" class="total-price__tax">（税込）</span>
          </div>
          <div class="gift-card-price__total--price">
            ¥{{ currentGiftCardPatternPrice.toLocaleString() }}
          </div>
        </div>
        <div class="gift-card-price__summary mt-8">
          <div class="gift-card-price__summary--label">
            <span class="summary-price__label">決済累計金額</span>
            <span v-show="isSummaryPriceIncludingTax" class="summary-price__tax">（税込）</span>
            <span
              class="summary-price__breakdown" :class="{
                'tl-chevron-up--bold--right': showSummaryTotalPrice,
                'tl-chevron-down--bold--right': !showSummaryTotalPrice,
                'ml-8': !isSummaryPriceIncludingTax
              }"
              @click.prevent="onClickSummaryPrice"
            >
              内訳
            </span>
          </div>
          <div class="gift-card-price__summary--price">
            ¥{{ giftCardPatternSummaryPrice.toLocaleString() }}
          </div>
        </div>
        <div 
          v-show="showSummaryTotalPrice"
          class="mt-8"
        >
          <div
            v-for="giftCardPatternPrice in giftCardPatternPrices"
            :key="giftCardPatternPrice.title"
            class="gift-card-price__other-total"
          >
            <div class="gift-card-price__other-total--label">
              {{ giftCardPatternPrice.title }} <span v-if="giftCardPatternPrice.isTotalPriceIncludingTax">（税込）</span>
            </div>
            <div class="gift-card-price__other-total--price">
              ¥{{ giftCardPatternPrice.totalPrice.toLocaleString() }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="bottom-container">
      <div class="gift-card-bottom">
        <form
          v-if="!isNewGiftCard"
          name="deleteForm"
          method="post"
          :action="
            `/bulk_order/gift_cards/${giftCardSessionToken}`
          "
        >
          <input type="hidden" name="authenticity_token" :value="csrfToken" />
          <input type="hidden" name="_method" value="DELETE" />
          <input type="submit" class="gift-card-bottom__submit" />
        </form>
        <div>
          <button
            class="gift-card-bottom__delete-button"
            @click="onClickDeleteGiftCardButton"
          >
            このカードを削除してリストへもどる
          </button>
        </div>
      </div>
      <TextModal
        v-show="isDeleteGiftCardModalActive"
        :title-text="`ギフトカード\n「${giftCardForm.title}」\nを削除しますか？`"
        primary-button-text="削除する"
        cancel-button-text="キャンセル"
        @clicked-modal-cover="closeDeleteGiftCardModal"
        @clicked-primary="onClickDeleteGiftCardModal"
        @clicked-cancel="closeDeleteGiftCardModal"
      />
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { Form } from 'vee-validate'
import EditableTicket from '@/src/js/customer/components/molecules/tickets/EditableTicket.vue'
import ButtonSelectArea from '@/src/js/customer/components/atoms/buttons/ButtonSelectArea.vue'
import FileUploadArea from '@/src/js/customer/components/atoms/file_upload_area/FileUploadArea.vue'
import PageHeadlineForSection from '@/src/js/customer/components/molecules/headlines/PageHeadlineForSection.vue'
import PageHeadlineForBulkOrder, {
  ButtonType
} from '@/src/js/customer/components/organism/bulk_personal_orders/PageHeadlineForBulkOrder.vue'
import InputFormText from '@/src/js/customer/components/atoms/form/InputFormText.vue'
import InputFormTextArea from '@/src/js/customer/components/atoms/form/InputFormTextArea.vue'
import InputFormNumber from '@/src/js/customer/components/atoms/form/InputFormNumber.vue'
import EditOutlineImage from '@/src/images/icons/edit-outline.svg'
import FlexibleModal from '@/src/js/customer/components/molecules/modals/flexible-modal.vue'
import TextModal from '@/src/js/customer/components/molecules/modals/text-modal.vue'
import ButtonPrimary from '@/src/js/customer/components/molecules/buttons/ButtonPrimary.vue'
import ErrorMessages from '~/src/js/customer/components/atoms/form/ErrorMessages.vue'
import { useBulkPersonalOrderSessionGiftCardStore } from '@/src/js/customer/store/bulkPersonalOrderSessionGiftCard'
import { cardDesignTypeConstants } from '@/src/js/customer/constants/bulkPersonalOrderConstants'
import { computed, onBeforeMount, onMounted, ref } from 'vue'
import TextLinkSelectArea from '@/src/js/customer/components/atoms/text_links/TextLinkSelectArea.vue'
import ImgLazy from '@/src/js/customer/components/atoms/images/ImgLazy.vue'
import { bulkOrderAgreementChecker } from '@/src/js/customer/pages/bulk_order/common/bulkOrderAgreementChecker'

const showablePatternEditModal = ref(false)
const errorMessages = ref([])
const fileErrorMessages = ref([])
const draftTitle = ref('')
const isDraftPatternTitleFocused = ref(false)
const isDeleteItemSkuModalActive = ref(false)
const isDeleteGiftCardModalActive = ref(false)
const deleteTargetItemSkuId = ref(null)
const showSummaryTotalPrice = ref(false)
const isSubmitting = ref(false)

const props = defineProps({
  bulkPersonalOrderSessionId: {
    type: Number,
    required: true
  },
  giftCardSessionToken: {
    type: String,
    required: false,
    default: 'new' // 新規作成の時は、new
  },
  patternTitle: {
    type: String,
    required: true
  },
  cardDesignId: {
    type: String,
    required: false,
    default: ''
  },
  cardDesignImageUrl: {
    type: String,
    required: false,
    default: ''
  },
  personalCardDesignId: {
    type: String,
    required: false,
    default: ''
  },
  personalCardDesignFileName: {
    type: String,
    required: false,
    default: ''
  },
  personalCardDesignDataUri: {
    type: String,
    required: false,
    default: ''
  },
  message: {
    type: String,
    required: false,
    default: ''
  },
  senderName: {
    type: String,
    required: false,
    default: ''
  },
  publishAmount: {
    type: Number,
    required: false,
    default: null
  },
  itemSkuContentAttributes: {
    type: Array,
    required: false,
    default: () => []
  },
  otherGiftCardPatternPrices: {
    type: Array,
    required: true
  },
  boxListErrorMessages: {
    type: Array,
    required: false,
    default: () => []
  }
})

onBeforeMount(() => {
  // まとめてオーダーの注意事項に同意していない場合は、ギフトカードリストへ戻す
  bulkOrderAgreementChecker()
})

onMounted(() => {
  errorMessages.value = props.boxListErrorMessages
})

const csrfToken = document
  .querySelector('meta[name="csrf-token"]')
  .getAttribute('content')

const cardDesignUrl = `/bulk_order/card_designs?token=${props.giftCardSessionToken}`

const bulkPersonalOrderSessionGiftCardStore = useBulkPersonalOrderSessionGiftCardStore()

const {
  setGiftCardForm,
  setTitle,
  setIsCardDesign,
  setIsAgreementForPersonalCardDesign,
  setPersonalCardDesign,
  setMessage,
  setSenderName,
  setPublishAmount,
  deleteItemSkuContents,
  countUpItemSkuContent,
  countDownItemSkuContent,
  deleteGiftCard
} = bulkPersonalOrderSessionGiftCardStore

const { giftCardForms, validForm } = storeToRefs(
  bulkPersonalOrderSessionGiftCardStore
)

const giftCardForm = computed(
  () => giftCardForms.value[props.giftCardSessionToken]
)

const isNewGiftCard = computed(() => props.giftCardSessionToken === 'new')

// 編集or作成中ギフトカード1枚あたりの合計金額
const currentGiftCardPrice = computed(() => {
  return giftCardForm.value.itemSkuContentAttributes.reduce(
    (sum, itemSkuContentAttribute) =>
      sum + itemSkuContentAttribute.price * itemSkuContentAttribute.quantity,
    0
  )
})

// 編集or作成中のパターンの合計金額
const currentGiftCardPatternPrice = computed(() => {
  return currentGiftCardPrice.value * giftCardForm.value.publishAmount
})

const isCurrentGiftCardPatternPriceIncludingTax = computed(() => {
  return giftCardForm.value.itemSkuContentAttributes.some(
    itemSkuContentAttribute => itemSkuContentAttribute.isIncludingTax
  )
})

const isSummaryPriceIncludingTax = computed(() => {
  const hasOtherPatternPriceIncludingTax = giftCardPatternPrices.value.some(
    giftCardPatternPrice => giftCardPatternPrice.isTotalPriceIncludingTax
  )
  return (
    isCurrentGiftCardPatternPriceIncludingTax.value ||
    hasOtherPatternPriceIncludingTax
  )
})

// 決済累計金額（その他のパターンの金額合計 ＋ 編集or作成中のパターンの合計金額）
const giftCardPatternSummaryPrice = computed(() => {
  return (
    props.otherGiftCardPatternPrices.reduce(
      (sum, otherGiftCardPatternPrice) =>
        sum + otherGiftCardPatternPrice.totalPrice,
      0
    ) + currentGiftCardPatternPrice.value
  )
})

const giftCardPatternPrices = computed(() => {
  return [
    {
      title: giftCardForm.value.title,
      totalPrice: currentGiftCardPatternPrice.value,
      isTotalPriceIncludingTax: isCurrentGiftCardPatternPriceIncludingTax.value
    },
    ...props.otherGiftCardPatternPrices
  ]
})

const isCardDesign = computed(() => {
  return giftCardForm.value.isCardDesign
})

const isPersonalCardDesign = computed(() => {
  return !giftCardForm.value.isCardDesign
})

const hasCardDesign = computed(() => {
  return (
    giftCardForm.value.cardDesignId !== '' &&
    giftCardForm.value.cardDesignImageUrl !== ''
  )
})

const hasPersonalCardDesignError = computed(
  () => fileErrorMessages.value.length > 0
)

const hasUploadedPersonalCardDesignImage = computed(() => {
  return (
    giftCardForm.value.personalCardDesignFileName !== '' &&
    giftCardForm.value.personalCardDesignDataUri !== ''
  )
})

const isDraftPatternTitleInputted = computed(() => {
  return draftTitle.value !== ''
})

const cardDesignType = computed({
  get() {
    return giftCardForm.value.isCardDesign
      ? cardDesignTypeConstants.NORMAL
      : cardDesignTypeConstants.PERSONAL
  },
  set(value) {
    const isCardDesign = value == cardDesignTypeConstants.NORMAL ? true : false
    setIsCardDesign(props.giftCardSessionToken, isCardDesign)
  }
})

// 存在しないギフトカードの場合は、初期値（新規作成：初期値、編集：現在の状態）を入れる
if (giftCardForm.value === undefined) {
  setGiftCardForm({
    giftCardSessionToken: props.giftCardSessionToken,
    title: props.patternTitle,
    isCardDesign: props.personalCardDesignId === '',
    cardDesignId: props.cardDesignId,
    cardDesignImageUrl: props.cardDesignImageUrl,
    personalCardDesignId: props.personalCardDesignId,
    personalCardDesignFileName: props.personalCardDesignFileName,
    personalCardDesignDataUri: props.personalCardDesignDataUri,
    isAgreementForPersonalCardDesign: props.personalCardDesignId !== '',
    message: props.message,
    senderName: props.senderName,
    publishAmount: props.publishAmount,
    itemSkuContentAttributes: props.itemSkuContentAttributes
  })
}

const onClickHeaderPatternEditButton = () => {
  draftTitle.value = ''
  showablePatternEditModal.value = true
}
const closeHeaderPatternEditModal = () => {
  showablePatternEditModal.value = false
}
const updatePatternTitle = async () => {
  showablePatternEditModal.value = false
  setTitle(props.giftCardSessionToken, draftTitle.value)
}
const onDraftPatternTitleFocus = () => {
  isDraftPatternTitleFocused.value = true
}
const onDraftPatternTitleBlur = () => {
  isDraftPatternTitleFocused.value = false
}
const onChangeMessage = event => {
  setMessage(props.giftCardSessionToken, event.target.value)
}
const onChangeSenderName = event => {
  setSenderName(props.giftCardSessionToken, event.target.value)
}
const onChangePublishAmount = event => {
  // 発行数は指定の範囲外の値を入力可能なので、「プレビューに進む」押下後に値を変更すると、バリデーションを抜けることが可能
  // そのため、「プレビューに進む」押下後は値を更新しない
  if (isSubmitting.value) return

  setPublishAmount(props.giftCardSessionToken, event.target.value)
}

const onClickCardDesignButton = () => {
  window.location.href = cardDesignUrl
}

const onClickChangeCardDesign = () => {
  window.location.href = cardDesignUrl
}

const onChangeIsAgreementForPersonalCardDesign = () => {
  setIsAgreementForPersonalCardDesign(
    props.giftCardSessionToken,
    !giftCardForm.value.isAgreementForPersonalCardDesign
  )
}
const onChangePersonalCardDesignFile = async e => {
  fileErrorMessages.value = []
  const files = e.target.files || e.dataTransfer.files
  const file = files[0]

  const validFileSize = validationFileSize(file.size)
  const validFileType = validationFileType(file.type)
  const validAspectRatio = await validationFileAspectRatio(file)

  if (validFileSize && validFileType && validAspectRatio) {
    const dataUri = await imageDataUriConverter(file)
    setPersonalCardDesign(props.giftCardSessionToken, file.name, dataUri)
  }
}
const imageDataUriConverter = async file => {
  return await new Promise(resolve => {
    const reader = new FileReader()
    reader.onload = () => {
      resolve(reader.result)
    }
    reader.readAsDataURL(file)
  })
}
const validationFileAspectRatio = async file => {
  const { width, height } = await getImageWidthAndHeight(file)
  const isSameRatio = width === height
  if (!isSameRatio) {
    fileErrorMessages.value.push(
      '縦横比が1対1（正方形）のものを選択してください'
    )
  }
  return isSameRatio
}
const validationFileSize = fileSize => {
  if (fileSize > 4 * 1024 * 1024) {
    fileErrorMessages.value.push('容量が4mb以下のものを選択してください')
    return false
  }
  return true
}
const validationFileType = fileType => {
  const allowedExtensions = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/gif'
  ]
  if (!allowedExtensions.includes(fileType)) {
    fileErrorMessages.value.push(
      'JPG/PNG/GIFのいずれかの形式のものを選択してください'
    )
    return false
  }
  return true
}
const getImageWidthAndHeight = async file => {
  return await new Promise(resolve => {
    const image = new Image()
    image.onload = () => {
      const size = {
        width: image.naturalWidth,
        height: image.naturalHeight
      }

      URL.revokeObjectURL(image.src)

      resolve(size)
    }
    image.src = URL.createObjectURL(file)
  })
}

const onClickCountUp = itemSkuId => {
  countUpItemSkuContent(props.giftCardSessionToken, itemSkuId)
}

const onClickCountDown = itemSkuId => {
  countDownItemSkuContent(props.giftCardSessionToken, itemSkuId)
}

const closeDeleteItemSkuModal = () => {
  deleteTargetItemSkuId.value = null
  isDeleteItemSkuModalActive.value = false
}

const onClickedPrimaryDeleteItemSkuModal = () => {
  dataLayer.push({
    event: 'remove_from_bulk_cart',
    bulk_personal_order_session_id: props.bulkPersonalOrderSessionId,
    item_sku_id: deleteTargetItemSkuId.value
  })

  deleteItemSkuContents(props.giftCardSessionToken, deleteTargetItemSkuId.value)

  deleteTargetItemSkuId.value = null
  isDeleteItemSkuModalActive.value = false
}

const onClickDeleteItemSkuButton = itemSkuId => {
  deleteTargetItemSkuId.value = itemSkuId
  isDeleteItemSkuModalActive.value = true
}

const onClickSummaryPrice = () => {
  showSummaryTotalPrice.value = !showSummaryTotalPrice.value
}

const onClickDeleteGiftCardButton = () => {
  isDeleteGiftCardModalActive.value = true
}

const closeDeleteGiftCardModal = () => {
  isDeleteGiftCardModalActive.value = false
}

const onClickDeleteGiftCardModal = () => {
  dataLayer.push({
    event: 'click_bulk_gift_card_edit__delete',
    bulk_personal_order_session_id: props.bulkPersonalOrderSessionId
  })

  // FIXME: 遷移完了より前にpiniaのデータを削除してしまうため、遷移が完了するまで、描画がヘッダーだけになってしまう
  deleteGiftCard(props.giftCardSessionToken)

  // 新規作成のギフトカードの場合は、ギフトカード一覧画面戻る
  if (isNewGiftCard.value) {
    window.location.href = '/bulk_order/gift_cards'
  } else {
    // 編集のギフトカードの場合は、フォーム送信する
    document.deleteForm.submit()
  }
}

const onClickAddGiftButton = () => {
  window.location.href = `/bulk_order/items?token=${props.giftCardSessionToken}`
}

const validateGiftCard = () => {
  const errors = []
  const MAX_ITEM_SKU_QUANTITY = 10
  const itemSkuTotalAmount = giftCardForm.value.itemSkuContentAttributes.reduce(
    (previous, current) => {
      return previous + current.quantity
    },
    0
  )

  if (itemSkuTotalAmount > MAX_ITEM_SKU_QUANTITY) {
    errors.push(`${MAX_ITEM_SKU_QUANTITY + 1}枚以上のギフトが選択されています`)
  }
  return errors
}

const onClickPreviewButton = () => {
  isSubmitting.value = true
  const errors = validateGiftCard()
  if (errors.length > 0) {
    errorMessages.value = errors
    isSubmitting.value = false
    // エラーがあった場合は、ページ上部に移動する
    window.scroll({
      top: 0,
      behavior: 'smooth'
    })

    return
  }

  window.location.href = isNewGiftCard.value
    ? '/bulk_order/gift_cards/preview'
    : `/bulk_order/gift_cards/${props.giftCardSessionToken}/preview`
}
</script>
<style lang="scss" scoped>
.bulk-order-gift-cards-edit {
  .container-header {
    display: flex;
    justify-content: center;
    background-color: $white;
    margin-bottom: 16px;
  }

  .container {
    display: flex;
    justify-content: center;
    background-color: $white;
  }

  .bottom-container {
    display: flex;
    justify-content: center;
  }

  .gift-card-header {
    max-width: $small-contents-width;
    width: 100%;
  }

  .gift-card {
    max-width: $small-contents-width;
    width: 100%;
    padding: 24px 16px;
    box-sizing: border-box;
    text-align: left;

    &__alert-box {
      padding: 16px;
      margin-bottom: 16px;
      background-color: #faeae9;
      border: 1px solid $danger;
      border-radius: 4px;

      &--message {
        font-size: 1rem;
        line-height: 1.6;
        color: $danger;
        text-align: left;
      }
    }

    &__required_caution {
      font-size: 1.2rem;
      color: $danger;
      line-height: 1.6;

      &::after {
        font-size: 1.2rem;
        line-height: 1.6;
        content: '*';
        color: $danger;
      }
    }

    &__section {
      margin-bottom: 40px;

      &--content {
        margin-top: 16px;
      }
    }

    &__form {
      display: flex;
      justify-content: flex-start;
      align-items: center;

      &--errors {
        margin-top: 8px;
      }
    }

    &__caution {
      font-size: 1.2rem;
      font-weight: 700;
      color: $black;
      line-height: 1.6;

      &::after {
        font-size: 1.2rem;
        line-height: 1.6;
        content: '*';
        color: $danger;
      }
    }

    &__notice {
      font-size: 1rem;
      line-height: 1.6;
      color: $gray-1;
    }

    &__note {
      font-size: 1rem;
      line-height: 1.6;
      color: $gray-1;
      padding: 8px;
      background-color: $gray-3;
      border: 1px solid $gray-2;
      border-radius: 4px;
    }

    &__card-design-image {
      width: 100%;
      object-fit: contain;
    }

    &__radio {
      border-bottom: none !important;
      padding: 0;
    }

    &__agreement-check {
      display: block;
      margin-bottom: 16px;
    }

    &__personal-card-design-content {
      display: flex;
      justify-content: center;
      flex-direction: column;
    }

    &__card-design-content {
      display: flex;
      justify-content: center;
      flex-direction: column;
    }

    &__card-design-change-link {
      margin: 16px auto 0;

      text-align: center;

      a {
        display: inline;
      }
    }

    &__sync-outline {
      margin-left: 4px;
      padding-left: 24px;

      @include bg-settings(
        '../../../../../../images/icons/sync-outline.svg',
        $white,
        20px,
        18px
      );
    }

    &__card-design-error-message {
      margin-bottom: 8px;
      font-size: 1.2rem;
      line-height: 1.6;
      color: $danger;
    }

    &__select-items {
      // このボタンは、横のpadding(16px)が不要なので、その分横幅追加して、表示領域を増やす
      width: calc(100% + 32px);
      margin: 0 -16px;
    }
  }

  .gift-card-modal {
    &__caption {
      font-size: 1.2rem;
      line-height: 1.6;
      color: $black;
      text-align: center;
      margin-bottom: 16px;
    }

    &__image {
      width: 100%;
      margin-bottom: 24px;
    }

    &__label {
      font-size: 1.2rem;
      line-height: 1.6;
      color: $black;
      text-align: left;
      margin-bottom: 8px;
    }

    &__notice {
      margin: 8px 0 24px 0;
      font-size: 1rem;
      line-height: 1.6;
      color: $gray-1;
      text-align: center;
    }
  }

  .select-item__box {
    &:not(:first-child) {
      padding-top: 16px;
    }

    &:not(:last-child) {
      border-bottom: 1px $gray-2 solid;
      padding-bottom: 16px;
    }

    &:last-child {
      padding-bottom: 16px;
    }
  }

  .gift-card-price {
    max-width: $small-contents-width;
    width: 100%;
    padding: 24px 16px;
    box-sizing: border-box;

    &__total {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &--label {
        display: flex;
        align-items: center;
      }

      &--price {
        font-size: 1.4rem;
        font-weight: bold;
        line-height: 1.6;
      }
    }

    &__summary {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &--label {
        display: flex;
        align-items: center;
      }

      &--price {
        font-size: 1.4rem;
        font-weight: bold;
        line-height: 1.6;
      }
    }

    &__other-total {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 4px;

      &--label {
        font-size: 1.2rem;
        line-height: 1.6;
        color: $black;
      }

      &--price {
        font-size: 1.2rem;
        line-height: 1.6;
        color: $black;
      }
    }

    .total-price {
      &__label {
        font-size: 1.4rem;
        font-weight: bold;
        line-height: 1.6;
        color: $black;
      }

      &__tax {
        font-size: 1.4rem;
        line-height: 1.6;
        color: $black;
      }
    }

    .summary-price {
      &__label {
        font-size: 1.4rem;
        font-weight: bold;
        line-height: 1.6;
        color: $black;
      }

      &__tax {
        font-size: 1.4rem;
        line-height: 1.6;
        color: $black;
      }

      &__breakdown {
        cursor: pointer;
        font-size: 1.4rem;
        line-height: 1.6;
        color: #5187ad;
      }
    }
  }

  .gift-card-bottom {
    max-width: $small-contents-width;
    width: 100%;
    margin: 16px auto;

    &__delete-button {
      font-size: 1.6rem;
      line-height: 1.6;
      color: $link;
      padding: 16px;
      width: 100%;
      cursor: pointer;
    }

    &__submit {
      display: none;
    }
  }

  .gift-card-fixed-bottom {
    display: flex;
    justify-content: center;
    align-items: center;
    position: fixed;
    z-index: 1;
    bottom: 0;
    width: 100%;
    background-color: $white;

    &__content {
      max-width: $small-contents-width;
      width: 100%;
      padding: 24px 16px;
      box-sizing: border-box;
    }
  }
}
</style>

<style>
/* 
 * 「プレビューに進む」ボタンが下部に固定のため、フッターが見えない。
 * そのため、見えるようにするために余白を調整
 */
footer {
  padding-bottom: 100px !important;
}
</style>
